(() => {
  const STORAGE_KEY = 'teveo-wishlist-variant-ids';
  const BUTTON_SELECTOR = '[data-wishlist-button], .teveo-wishlist-button';
  const ACTIVE_CLASS = 'is-active';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  const WINDOW_NAME_PREFIX = 'teveoWishlist:';
  const CUSTOMER_ID = String(window.teveoWishlistCustomerId || '');
  let wishlistVariantIds = null;

  const collectVariantIds = (item) => {
    if (Array.isArray(item)) {
      return item.flatMap(collectVariantIds);
    }

    if (item && typeof item === 'object') {
      const nestedItems = [
        ...(Array.isArray(item.all) ? item.all : []),
        ...(Array.isArray(item.items) ? item.items : []),
        ...(Array.isArray(item.products) ? item.products : [])
      ];

      return [
        item.epi,
        item.variantId,
        item.variant_id,
        item.variant?.id,
        String(item.id || '').match(/^gid:\/\/shopify\/ProductVariant\/\d+$/) ? item.id : null,
        ...nestedItems.flatMap(collectVariantIds)
      ];
    }

    return String(item || '').split(',');
  };

  const normalizeVariantIds = (value) => {
    return collectVariantIds(value)
      .map((id) => String(id || '').trim())
      .map((id) => id.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/)?.[1] || id)
      .filter((id) => /^\d+$/.test(id))
      .filter((id, index, list) => list.indexOf(id) === index);
  };

  const parseStoredWishlistValue = (value) => {
    if (!value) {
      return [];
    }

    try {
      return normalizeVariantIds(JSON.parse(value));
    } catch {
      return normalizeVariantIds(value);
    }
  };

  const getCookieValue = (key) => {
    const encodedKey = `${encodeURIComponent(key)}=`;
    const value = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(encodedKey))
      ?.slice(encodedKey.length) || '';
    return value ? decodeURIComponent(value) : '';
  };

  const setCookieValue = (key, value) => {
    document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  };

  const removeCookieValue = (key) => {
    document.cookie = `${encodeURIComponent(key)}=; path=/; max-age=0; SameSite=Lax`;
  };

  const getStoredValue = (key) => {
    try {
      return window.localStorage?.getItem(key) || getCookieValue(key) || getWindowNameValue();
    } catch {
      return getCookieValue(key) || getWindowNameValue();
    }
  };

  const getWindowNameValue = () => {
    return window.name.startsWith(WINDOW_NAME_PREFIX)
      ? window.name.slice(WINDOW_NAME_PREFIX.length)
      : '';
  };

  const setWindowNameValue = (value) => {
    window.name = value ? `${WINDOW_NAME_PREFIX}${value}` : '';
  };

  const getStoredVariantIds = (key) => {
    return parseStoredWishlistValue(getStoredValue(key));
  };

  const getQueryVariantIds = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return normalizeVariantIds([
      ...searchParams.getAll('variantIds'),
      ...searchParams.getAll('variantId'),
      searchParams.get('variants')
    ]);
  };

  const getWishlist = () => {
    if (Array.isArray(wishlistVariantIds)) {
      return [...wishlistVariantIds];
    }

    const storedVariantIds = getStoredVariantIds(STORAGE_KEY);
    if (storedVariantIds.length > 0) {
      wishlistVariantIds = storedVariantIds;
      return [...wishlistVariantIds];
    }

    const queryVariantIds = getQueryVariantIds();
    if (queryVariantIds.length > 0) {
      wishlistVariantIds = queryVariantIds;
      return [...wishlistVariantIds];
    }

    return [];
  };

  const setWishlist = (variantIds) => {
    const nextVariantIds = normalizeVariantIds(variantIds);
    const value = JSON.stringify(nextVariantIds);
    wishlistVariantIds = nextVariantIds;

    try {
      if (nextVariantIds.length > 0) {
        window.localStorage?.setItem(STORAGE_KEY, value);
      } else {
        window.localStorage?.removeItem(STORAGE_KEY);
      }
    } catch {}

    if (nextVariantIds.length > 0) {
      setCookieValue(STORAGE_KEY, value);
      setWindowNameValue(value);
    } else {
      removeCookieValue(STORAGE_KEY);
      setWindowNameValue('');
    }

    window.dispatchEvent(new CustomEvent('teveo:wishlist:updated', {
      detail: {variantIds: nextVariantIds}
    }));

    return nextVariantIds;
  };

  const isWishlistPage = () => {
    return window.location.pathname.endsWith('/pages/wishlist');
  };

  const getButtonVariantId = (button) => {
    if (button.closest('.wishlist-card')) {
      return normalizeVariantIds(button.dataset.variantId)[0];
    }

    const productForm = button.closest('form[action*="/cart/add"]');
    const selectedVariantInput = productForm?.querySelector('[name="id"]');
    return normalizeVariantIds(selectedVariantInput?.value || button.dataset.variantId)[0];
  };

  const syncWishlistOperation = (operation, variantIds) => {
    return fetch('/apps/wishlist', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        operation,
        variantIds: normalizeVariantIds(variantIds)
      })
    }).catch((error) => {
      console.error('Error syncing wishlist:', error);
    });
  };

  const getLocalePrefix = () => {
    const rootUrl = window.routes?.root_url || '';
    if (rootUrl && rootUrl !== '/') {
      return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    }

    const localePath = window.location.pathname.match(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i)?.[0];
    return localePath ? `${localePath}/` : '/';
  };

  const getWishlistHref = (variantIds = getWishlist()) => {
    const wishlistUrl = new URL(`${getLocalePrefix()}pages/wishlist`, window.location.origin);
    const normalizedVariantIds = normalizeVariantIds(variantIds);
    if (normalizedVariantIds.length > 0) {
      wishlistUrl.searchParams.set('variants', normalizedVariantIds.join(','));
    }
    return `${wishlistUrl.pathname}${wishlistUrl.search}`;
  };

  const syncCurrentWishlistUrl = (variantIds = getWishlist()) => {
    if (!isWishlistPage()) {
      return;
    }

    const nextHref = getWishlistHref(variantIds);
    const currentHref = `${window.location.pathname}${window.location.search}`;
    if (currentHref !== nextHref) {
      window.history.replaceState({}, '', nextHref);
    }
  };

  const updateHeaderCount = (variantIds = getWishlist()) => {
    const header = document.querySelector('[data-wishlist-header], .header__icon--wishlist');
    const bubble = header?.querySelector('.wishlistbuble');
    const countElements = bubble?.querySelectorAll('span');

    if (!header || !bubble || !countElements) {
      return;
    }

    header.href = getWishlistHref(variantIds);

    const count = variantIds.length;
    header.classList.toggle(ACTIVE_CLASS, count > 0);
    bubble.style.display = count > 0 ? 'block' : 'none';
    countElements.forEach((element) => {
      element.textContent = count;
      element.classList.toggle('visually-hidden', count === 0);
    });
  };

  const updateButtons = () => {
    const variantIds = getWishlist();
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      const variantId = getButtonVariantId(button);
      const isActive = variantIds.includes(variantId);
      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.querySelector('.heart-icon')?.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    updateHeaderCount(variantIds);
  };

  const syncLoggedInWishlist = () => {
    if (!CUSTOMER_ID) {
      return;
    }

    const localVariantIds = getWishlist();
    syncWishlistOperation('merge', localVariantIds).then((response) => {
      if (!response?.ok) {
        return;
      }

      response.json().then((payload) => {
        if (!Array.isArray(payload.variantIds)) {
          return;
        }

        const mergedVariantIds = setWishlist(payload.variantIds);
        updateButtons();
        syncCurrentWishlistUrl(mergedVariantIds);

        if (payload.changed && isWishlistPage()) {
          window.location.reload();
        }
      }).catch(() => {});
    });
  };

  const getToastImage = (button) => {
    const card = button.closest('.card-wrapper, .wishlist-card, .grid__item');
    const cardImage = card?.querySelector('img');
    const productImage = document.querySelector('media-gallery .product__media-item.is-active img')
      || document.querySelector('media-gallery img');
    const image = cardImage || productImage;

    return {
      url: image?.currentSrc || image?.src || image?.dataset?.src || '',
      alt: image?.alt || ''
    };
  };

  const showWishlistAddedToast = (button) => {
    if (typeof window.showToast !== 'function') {
      return;
    }

    const image = getToastImage(button);
    const wishlistMessage = document.querySelector('toast-notification')?.dataset.wishlistMessage || 'Added to your wishlist';
    window.showToast({
      message: wishlistMessage,
      duration: 3200,
      imageUrl: image.url,
      imageAlt: image.alt,
      variant: 'wishlist',
      anchorSelector: '[data-wishlist-header]',
      actionUrl: getWishlistHref()
    });
  };

  const toggleWishlist = (button) => {
    const variantId = getButtonVariantId(button);
    if (!variantId) {
      return;
    }

    const currentVariantIds = getWishlist();
    const isActive = currentVariantIds.includes(variantId);
    const nextVariantIds = isActive
      ? currentVariantIds.filter((id) => id !== variantId)
      : [...currentVariantIds, variantId];

    setWishlist(nextVariantIds);
    updateButtons();
    syncCurrentWishlistUrl(nextVariantIds);
    if (!isActive) {
      showWishlistAddedToast(button);
    }
    syncWishlistOperation(isActive ? 'remove' : 'add', [variantId]).then((response) => {
      if (response?.ok) {
        response.json().then((payload) => {
          if (Array.isArray(payload.variantIds)) {
            setWishlist(payload.variantIds);
            updateButtons();
            syncCurrentWishlistUrl(payload.variantIds);
          }
        }).catch(() => {});
      }
    });

    button.dispatchEvent(new CustomEvent('teveo:wishlist:toggled', {
      bubbles: true,
      detail: {
        isActive: !isActive,
        variantId,
        variantIds: nextVariantIds,
      },
    }));
  };

  let initialized = false;

  const initWishlist = () => {
    if (initialized) {
      return;
    }

    initialized = true;
    updateButtons();
    syncLoggedInWishlist();

    document.addEventListener('click', (event) => {
      const button = event.target.closest(BUTTON_SELECTOR);
      if (!button) {
        return;
      }

      event.preventDefault();
      toggleWishlist(button);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }

  window.setTimeout(() => {
    updateButtons();
  }, 1000);
  document.addEventListener('change', (event) => {
    if (event.target.matches('[name="id"]')) {
      window.setTimeout(updateButtons, 0);
    }
  });
  document.addEventListener('theme:loading:end', () => window.setTimeout(updateButtons, 100));
  window.addEventListener('teveo:wishlist:updated', (event) => {
    updateButtons();
    updateHeaderCount(event.detail?.variantIds);
    syncCurrentWishlistUrl(event.detail?.variantIds);
  });
})();
